import { createContext, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { setApiLocale } from '@/lib/api/client';
import { storageKeys } from '@/lib/config';
import { readStorage, writeStorage } from '@/lib/storage';
import type { Locale } from '@/types';
import { defaultLocale, dictionaries, htmlLang, isLocale } from './index';
import type { TranslationKey } from './index';

export type TranslateValues = Record<string, string | number>;

export interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, values?: TranslateValues) => string;
}

export const I18nContext = createContext<I18nContextValue | null>(null);

const detectLocale = (): Locale => {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const param = (params.get('lang') || params.get('locale'))?.toLowerCase();
    if (param) {
      const normalized = param === 'kk' ? 'kz' : param;
      if (isLocale(normalized)) return normalized;
    }
  }
  const stored = readStorage<string | null>(storageKeys.locale, null);
  if (isLocale(stored)) return stored;
  if (typeof navigator !== 'undefined') {
    const nav = navigator.language.slice(0, 2).toLowerCase();
    if (nav === 'kk') return 'kz';
    if (isLocale(nav)) return nav;
  }
  return defaultLocale;
};

const interpolate = (template: string, values?: TranslateValues): string => {
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (match, token: string) => {
    const value = values[token];
    return value === undefined ? match : String(value);
  });
};

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  useLayoutEffect(() => {
    setApiLocale(locale);
  }, [locale]);

  useEffect(() => {
    document.documentElement.lang = htmlLang[locale];
    writeStorage(storageKeys.locale, locale);
  }, [locale]);

  const setLocale = useCallback((next: Locale) => setLocaleState(next), []);

  const t = useCallback(
    (key: TranslationKey, values?: TranslateValues) =>
      interpolate(dictionaries[locale][key] ?? dictionaries[defaultLocale][key] ?? key, values),
    [locale],
  );

  const value = useMemo<I18nContextValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};
